import React, { FC } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { ISubscriptionAuditEvent } from '../types/audit-event';
import { formatDate } from './EntitlementEventDetailDialog';

// identity_not_found → "Identity not found"
const formatFailureReason = (reason: string | null): string =>
  reason ? reason.charAt(0).toUpperCase() + reason.slice(1).replace(/_/g, ' ') : '-';

interface EntitlementsSyncDetailDialogProps {
  event: ISubscriptionAuditEvent | null;
  onClose: () => void;
}

export const EntitlementsSyncDetailDialog: FC<EntitlementsSyncDetailDialogProps> = ({
  event,
  onClose,
}) => {
  const properties = event?.properties;
  if (!event || !properties) return null;

  const { pushedCount, deletedCount, failures, expectedCount } = properties;
  const pendingCount = Math.max(expectedCount - pushedCount - deletedCount - failures.length, 0);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Entitlements sync details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2">{formatDate(event.dateOccurred)}</Typography>
            </Box>
            {event.correlationId && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Correlation ID
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                  {event.correlationId}
                </Typography>
              </Box>
            )}
          </Stack>

          <Typography variant="body2">{event.description}</Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {pushedCount > 0 && (
              <Chip label={`${pushedCount} granted`} size="small" color="success" />
            )}
            {deletedCount > 0 && (
              <Chip label={`${deletedCount} revoked`} size="small" color="info" />
            )}
            {failures.length > 0 && (
              <Chip label={`${failures.length} failed`} size="small" color="error" />
            )}
            {pendingCount > 0 && (
              <Chip
                label={`${pendingCount} pending`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>

          <Divider />

          {failures.length === 0 ? (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No failures.
            </Typography>
          ) : (
            // Only the table scrolls, so the summary above stays in view.
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  {/* Sticky header cells paint their own background. */}
                  <TableRow sx={{ '& th': { backgroundColor: 'grey.50' } }}>
                    <TableCell>Identity</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Failed at</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failures.map((failure) => (
                    <TableRow key={failure.identityId}>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/user/detail/${failure.identityId}`}
                          underline="hover"
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {failure.identityId}
                        </Link>
                      </TableCell>
                      <TableCell>{failure.roleName ?? '-'}</TableCell>
                      <TableCell>{formatFailureReason(failure.failureReason)}</TableCell>
                      <TableCell>{formatDate(failure.completedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
